import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { DateTime } from "luxon"
import { ColumnFiltersState } from "@tanstack/react-table"
import Table from "../../table"
import ManageDreamRatePanel from "../form/manage-panel"
import DreamRateEmptyState from "../empty-state"
import DaysFilter from "../../days-filter"
import HowItWorksModal from "../../how-it-works-modal"
import DreamRateFilters from "../filters"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { selectUserDetails } from "../../../../redux/slices/auth"
import { setVisiblePanel } from "../../../../redux/slices/app"
import {
  selectTabRange,
  updateRange
} from "../../../../redux/slices/engagements"
import {
  useFetchPendingSubscriptionByIdQuery,
  useFetchPendingSubscriptionsQuery
} from "../../../../redux/queries"
import {
  BBColumnDef,
  DateRange,
  EngagementsTab,
  PendingSubscriptionFull
} from "../../../../types"
import OwnerCell from "./cells/owner"
import EngagementsCell from "./cells/engagements"
import { Pencil, PlayCircle } from "lucide-react"
import { LinkBtn } from "../../buttons"
import ScreenReaderOnly from "../../screen-reader-only"
import { dreamRateFilter } from "../filters/dream-rate-filter"
import { getPendingSubscriptionStatus } from "../../../utils/dream-rate"
import { checkIsAdmin, checkIsSuperAdmin } from "../../../utils/user"

const DreamRateTable = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<
    number | undefined
  >(undefined)

  const userDetails = useAppSelector(selectUserDetails)
  const loid = userDetails?.id
  const orgId = userDetails?.org?.id
  const isAdmin = useMemo(
    () => checkIsAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )
  const isSuperAdmin = useMemo(
    () => checkIsSuperAdmin(userDetails?.roles ?? []),
    [userDetails?.roles]
  )

  const tabKey = EngagementsTab.dreamRate
  const range = useAppSelector(state => selectTabRange(state, tabKey))
  const handleUpdateRange = useCallback(
    (newValue: DateRange) => {
      dispatch(updateRange({ tabKey, newValue }))
    },
    [dispatch, tabKey]
  )

  const minCreated = useMemo(
    () => DateTime.now().minus({ days: range }),
    [range]
  )

  const { data: selectedSubscription } = useFetchPendingSubscriptionByIdQuery(
    { id: selectedSubscriptionId! },
    { skip: !selectedSubscriptionId }
  )

  // Note: Backend params may evolve; we pass commonly used filters when available.
  const { data: allRows = [], isFetching } = useFetchPendingSubscriptionsQuery({
    limit: 10000,
    offset: 0,
    ...(isSuperAdmin ? {} : isAdmin ? { org_id: orgId } : { loid })
  })

  // Client-side filtering by date since backend may not support min_created yet
  const rows = useMemo(() => {
    if (range === 1000) return allRows // "All time" - no filtering

    const minCreatedTimestamp = Math.floor(minCreated.toSeconds())
    return allRows.filter(row => {
      const rowCreated =
        typeof row.created === "number" ? row.created : parseInt(row.created)
      return rowCreated >= minCreatedTimestamp
    })
  }, [allRows, minCreated, range])

  // All-time existence check (limit 1) — only when current range is empty
  const isRangeEmpty = !isFetching && rows.length === 0
  const { data: anyHistoricalRows = [], isFetching: isFetchingAllTime } =
    useFetchPendingSubscriptionsQuery(
      {
        limit: 1,
        offset: 0,
        org_id: orgId,
        loid
      },
      { skip: !isRangeEmpty }
    )
  const hasAnyHistorical = (anyHistoricalRows?.length ?? 0) > 0

  // Show empty state only when not loading, no current rows, and no historical data exists
  const shouldShowEmptyState =
    !isFetching && !isFetchingAllTime && rows.length === 0 && !hasAnyHistorical

  const navigateToDetails = useCallback(
    (row: PendingSubscriptionFull) => {
      const status = getPendingSubscriptionStatus(row)
      const subscriptionId = Number(row.subscription_id ?? 0)
      if (status === "active" && subscriptionId !== 0) {
        navigate(`/engagements/${subscriptionId}`)
      } else {
        navigate(`/dream-rate/${row.id}`)
      }
    },
    [navigate]
  )

  const columns: BBColumnDef<PendingSubscriptionFull>[] = useMemo(
    () => [
      {
        id: "subscriber",
        header: "Subscriber",
        accessorFn: row => row.email,
        cell: props => (
          <EngagementsCell
            info={props.row.original}
            onClick={navigateToDetails}
          />
        ),
        colSpan: 2,
        enableColumnFilter: true,
        filterFn: dreamRateFilter
      },
      {
        id: "owner",
        header: "Owner",
        accessorFn: row => row.loid,
        footer: props => props.column.id,
        cell: props => (
          <OwnerCell info={props.row.original} onClick={navigateToDetails} />
        ),
        meta: { headerClassName: "hidden md:table-cell" }
      },
      {
        id: "created",
        header: "",
        accessorFn: row => row.created,
        footer: props => props.column.id,
        cell: props => "",
        colSpan: 0,
        meta: { headerClassName: "hidden" }
      },
      {
        id: "edit",
        header: "",
        footer: props => props.column.id,
        enableSorting: false,
        cell: props => {
          const status = getPendingSubscriptionStatus(props.row.original)
          if (status === "active") {
            return <td></td>
          }
          return (
            <td className="text-right">
              <LinkBtn
                onClick={() => setSelectedSubscriptionId(props.row.original.id)}
                className="text-neutral-400 hover:text-primary-400"
              >
                <Pencil size={20} />
                <ScreenReaderOnly>
                  Edit subscription {props.row.original.email}
                </ScreenReaderOnly>
              </LinkBtn>
            </td>
          )
        }
      }
    ],
    [navigateToDetails]
  )

  const [showHowItWorksModal, setShowHowItWorksModal] = useState(false)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])

  // Column filters state management

  const openLearnVideo = useCallback(() => setShowHowItWorksModal(true), [])

  const onNewRateDrop = useCallback(
    () => dispatch(setVisiblePanel("dream-rate/new")),
    [dispatch]
  )

  const resetSelectedSubscriptionId = useCallback(
    () => setSelectedSubscriptionId(undefined),
    []
  )

  const customFilterComponent = useMemo(
    () => (
      <DreamRateFilters
        columnID="subscriber"
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        className="flex items-center"
        shrinkFilter
      />
    ),
    [columnFilters]
  )

  return (
    <>
      {shouldShowEmptyState ? (
        <DreamRateEmptyState
          onLearnHowItWorks={openLearnVideo}
          onNewRateDrop={onNewRateDrop}
        />
      ) : (
        <Table<PendingSubscriptionFull>
          className="dream-rate-table"
          isLoading={isFetching}
          data={rows}
          columns={columns}
          viewType="table"
          dropdownFilterColumnId="subscriber"
          searchPlaceholder="Search subscribers…"
          extraHeaderElement={
            <DaysFilter range={range} handleUpdateRange={handleUpdateRange} />
          }
          extraHeaderElementClassName="ml-auto"
          extraElementBeforeTable={
            <div className="px-2">
              <LinkBtn className="-my-1" onClick={openLearnVideo}>
                <PlayCircle size={20} />
                <span className="ml-2">Learn how it works</span>
              </LinkBtn>
            </div>
          }
          shrinkFilterSort
          defaultSortBy="created"
          defaultSortDir="desc"
          hasColumnFilter={true}
          customFilterFns={{}}
          customFilterComponent={customFilterComponent}
          columnFilters={columnFilters}
          setColumnFilters={setColumnFilters}
        />
      )}

      <ManageDreamRatePanel
        open={!!(selectedSubscriptionId && selectedSubscription)}
        onClose={resetSelectedSubscriptionId}
        data={selectedSubscription}
      />
      <HowItWorksModal
        videoType="dream-rate"
        show={showHowItWorksModal}
        toggleModal={setShowHowItWorksModal}
      />
    </>
  )
}

export default DreamRateTable
